import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { GameVariant } from '@draughts/shared';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { channelDesc, type VariantChannel } from '../../content/variant-channels';
import { trackPartnerChannelClick } from '../../utils/analytics';
/* Solo l'indirizzo base, NON il client API: questo file sta nell'albero che il
   prerender carica da Node, e `services/api` si tirerebbe dietro Sentry. */
import { API_URL } from '../../services/api-url';
import '../dashboard/PlayModals.css';
import './Rules.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** La variante della pagina: accompagna il clic nella misura. */
  variant: GameVariant;
  channels: VariantChannel[];
}

/**
 * «Canali consigliati» — i canali YouTube di questa variante, uno sotto l'altro.
 *
 * Veste `play-online-modal`, la stessa pelle di Gioca e Problemi del giorno:
 * card centrata da 420px sul desktop, foglio a tutto schermo sotto i 768px.
 * Il foglio condiviso arriva da `dashboard/PlayModals.css` e non da
 * `DashboardPage.css`: quest'ultimo viaggia solo col pezzo pigro della
 * dashboard, e le pagine regole quel pezzo non lo caricano mai — l'overlay
 * resterebbe senza velo e senza card.
 *
 * ⚠️ Questa pagina la cuoce anche il prerender SSR: qui dentro tutto quello che
 * tocca `window` sta dentro un effetto, e a sipario chiuso il componente non
 * rende niente.
 */
/**
 * L'icona YouTube, come affordance del link — NON come avatar.
 *
 * Le linee guida del marchio consentono l'icona per *indicare un link a
 * contenuto YouTube*, ed e' esattamente questo il posto: sostituisce la
 * freccetta generica che diceva solo «si esce dal sito». Vietano invece di
 * usarla come identita' propria o in modo che implichi una partnership — ed e'
 * il motivo per cui NON sta al posto dell'avatar: li' diventerebbe l'identita'
 * visiva del canale, che non e' sua, e con piu' canali in elenco ogni riga
 * sarebbe identica, cioe' l'avatar perderebbe la sua unica funzione.
 *
 * ⚠️ Geometria e colori UFFICIALI, non reinterpretati: le linee guida vietano
 * di alterare forma, proporzioni e colore, quindi niente icona a tratto da una
 * libreria (`lucide-react`, che usiamo altrove, non ne ha una: nessuna
 * scorciatoia da cui farsi tentare) e niente rosso intonato alla nostra
 * palette. Il triangolo e' un path BIANCO e non un buco nel corpo rosso,
 * perche' su tema scuro un buco lo farebbe vedere scuro — cioe' non sarebbe
 * piu' il marchio. Il rosso pieno e' ammesso su fondo chiaro come su scuro.
 *
 * Largo 24px: le linee guida fissano un minimo di 20px.
 */
function IconaYouTube() {
  return (
    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
      <path
        fill="#FF0000"
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z"
      />
      <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}

export default function RulesChannelsOverlay({ isOpen, onClose, variant, channels }: Props) {
  const { t, i18n } = useTranslation();

  useOverlayBackButton(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Il fuoco entra nel dialogo, Tab non ne esce, alla chiusura torna a chi
  // l'aveva. Chiamato PRIMA del `return null`: un hook non sta dietro a una
  // condizione.
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen);

  if (!isOpen) return null;

  const title = t('rules.channels.title');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal dashboard-modal play-online-modal rl-channels-modal"
        ref={riferimentoDialogo}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="play-online-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="play-online-close"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <div className="play-online-body">
          <p className="rl-channels-note">{t('rules.channels.note')}</p>
          <div className="rl-ch-grid">
            {channels.map((c) => {
              const desc = channelDesc(c, i18n.language);
              return (
                <a
                  key={c.id}
                  className="rl-ch"
                  /*
                   * Non l'indirizzo del canale ma il NOSTRO rimbalzo, che conta
                   * il clic e poi gira (302) su YouTube. E' l'unico modo di
                   * avere il numero anche da chi rifiuta i cookie o blocca lo
                   * script di misura — e conta pure il tasto centrale e
                   * l'«apri in nuova scheda», che a un `onClick` sfuggono.
                   *
                   * Il prezzo: passando sopra il link si legge il nostro
                   * dominio, e se il nostro server e' giu' il canale non si
                   * apre. Su una pagina gia' servita da noi e' poco, ma non e'
                   * zero.
                   */
                  href={`${API_URL}/r/canale/${encodeURIComponent(c.id)}`}
                  /* ⚠️ L'indirizzo qui sopra e' NOSTRO, e quindi DENTRO lo
                     scope del manifest: il service worker lo vede come una
                     navigazione come un'altra e, se non e' escluso dal ripiego,
                     risponde lui con l'app shell invece di lasciarlo uscire —
                     il canale non si apre e si torna sulla nostra home. E'
                     successo davvero; la riga che lo impedisce sta in
                     `src/pwa/navigate-fallback-denylist.ts`, con il suo test.
                     (Il commento che stava qui ragionava sul fatto che YouTube
                     e' fuori scope: vero, e irrilevante — fuori scope e' la
                     destinazione del 302, non l'`href`.)

                     Con `_blank` la PWA installata resta viva dietro; nella
                     stessa scheda, su iOS in modalita' autonoma, si finisce in
                     un vicolo cieco senza barra ne' tasto indietro. Niente
                     `nofollow`: sono consigli redazionali. */
                  target="_blank"
                  rel={c.sponsored ? 'noopener noreferrer sponsored' : 'noopener noreferrer'}
                  aria-label={`${c.name} — ${t('rules.channels.opensOnYouTube')}`}
                  onClick={() => trackPartnerChannelClick(c.id, variant)}
                >
                  {/* L'iniziale con lo spread e non `charAt(0)`: su un nome che
                      comincia per emoji, `charAt` restituisce meta' coppia
                      surrogata. */}
                  <span className="rl-ch-ico" aria-hidden="true">{[...c.name][0]?.toUpperCase()}</span>
                  <span className="rl-ch-txt">
                    <span className="rl-ch-name">{c.name}</span>
                    {desc && <span className="rl-ch-desc">{desc}</span>}
                    <span className="rl-ch-meta">
                      <span className="rl-ch-flag" aria-hidden="true">{c.flag}</span>
                      <span className="rl-ch-handle">{c.handle}</span>
                    </span>
                  </span>
                  <span className="rl-ch-ext" aria-hidden="true"><IconaYouTube /></span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
